export class ApiError extends Error {
  readonly code: string
  readonly status: number
  constructor(code: string, status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

export type Me = { id: string; handle: string; avatarUrl: string | null; assistedInput: boolean }
export type Prompt = { text: string; weather: string; intensity: number }
export type VersionSummary = {
  id: string
  handle: string
  avatarUrl: string | null
  tokens: number
  prompt: string
  createdAt: number
  realMs: number
}
export type VersionDetail = VersionSummary & { chars: number; opCount: number; code: string }

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }

const JSON_HEADERS = { 'Content-Type': 'application/json' }

const unwrap = async <T>(response: Response): Promise<T> => {
  let body: Envelope<T> | undefined
  try {
    body = (await response.json()) as Envelope<T>
  } catch {
    throw new ApiError('bad_response', response.status, 'The server sent something unreadable.')
  }
  if (!body.ok) throw new ApiError(body.error.code, response.status, body.error.message)
  return body.data
}

export const createApi = (fetchImpl: typeof fetch = (...args) => fetch(...args)) => {
  const get = async <T>(path: string): Promise<T> =>
    unwrap<T>(await fetchImpl(path, { credentials: 'same-origin' }))
  const post = async <T>(path: string, body?: unknown): Promise<T | undefined> => {
    const response = await fetchImpl(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: body === undefined ? {} : JSON_HEADERS,
      body: body === undefined ? null : JSON.stringify(body),
    })
    if (response.status === 204) return undefined
    return unwrap<T>(response)
  }

  return {
    me: async (): Promise<Me | null> => {
      try {
        return await get<Me>('/api/me')
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null
        throw error
      }
    },
    prompt: () => get<Prompt>('/api/prompt'),
    listVersions: (limit = 50) => get<VersionSummary[]>(`/api/versions?limit=${limit}`),
    getVersion: (id: string) => get<VersionDetail>(`/api/versions/${encodeURIComponent(id)}`),
    getLog: async (id: string): Promise<Uint8Array> => {
      const response = await fetchImpl(`/api/versions/${encodeURIComponent(id)}/log`)
      if (!response.ok) throw new ApiError('not_found', response.status, 'Replay log not found.')
      return new Uint8Array(await response.arrayBuffer())
    },
    createVersion: (input: { code: string; log: string; prompt: string }) =>
      post<{ id: string }>('/api/versions', input) as Promise<{ id: string }>,
    recordView: (id: string) => post<undefined>(`/api/versions/${encodeURIComponent(id)}/views`),
    stats: (id: string) => get<{ views: number }>(`/api/versions/${encodeURIComponent(id)}/stats`),
    logout: () => post<undefined>('/auth/logout'),
  }
}

export type Api = ReturnType<typeof createApi>
